// «Аналитика продаж» — перенос раздела 📊 с портала 55.
//
// ── Чем этот экран отличается от всех остальных в CRM ──
//
// Все прочие разделы получают из ядра СТРАНИЦУ в 50 записей и считают суммы в браузере —
// поэтому под каждой цифрой там написано «по N из M». Здесь считает ядро, одним SQL по
// всей выборке (`analytics_api.py`), и цифры ТОЧНЫЕ. Так и сделано потому, что иначе
// раздела быть не может: в ядре 74 655 сделок (28.07.2026), «аналитика по 50 записям» —
// это не аналитика, а выкачивать всю базу в браузер ради одной суммы нельзя.
//
// ── Состав взят у портала 55, а не придуман ──
//
// `peleng-portal/src/worker.js::salesAnalytics` показывает: открытый портфель, воронку по
// стадиям, нагрузку по менеджерам и «за месяц: выиграно / проиграно / конверсия». Всё это
// здесь есть в том же смысле, потому что люди привыкли к этим цифрам, и расхождение
// пришлось бы объяснять. Добавлено то, чего у портала не было: по месяцам, по воронкам, по
// бизнес-юнитам, средний чек и сравнение с предыдущим периодом.
//
// Одно расхождение с порталом ЗАВЕДОМО есть, и оно в нашу пользу: портал листает открытые
// сделки циклом максимум на 12 страниц по 50 и на этом останавливается, а открытых сделок
// на 28.07.2026 — 3 062, то есть его «открытый портфель» — сумма по первым 600 сделкам,
// а наш — по всем. Про это написано на экране: иначе первый же, кто сверит две цифры,
// решит, что врёт новая CRM.
//
// ── Экран ТОЛЬКО ЧИТАЮЩИЙ ──
// Ни одной записи в Bitrix. Ни одной своей формулы по деньгам: всё, что не разметка,
// приходит от ядра или лежит в `lib/analytics.ts`.

import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { NotWired, fetchSalesAnalytics } from '../api/analytics'
import type { AnalyticsParams, PeriodKind, UnitRow } from '../api/analytics'
import { useFields, useRefs } from '../api/hooks'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import {
  PERIODS, deltaKind, emptyUnitShare, fmtDelta, fmtDeltaPp, fmtMonth, fmtNum, fmtPct,
  shareOfMax, unitLabel,
} from '../lib/analytics'
import { plural } from '../lib/dashboard'
import { fmtMoney } from '../lib/format'

/** Сколько строк показываем в разрезах «по людям». Полный список — в «Сделках». */
const ROWS = 15

export default function Analytics() {
  const [sp, setSp] = useSearchParams()
  const period = (sp.get('period') || 'year') as PeriodKind
  const category = sp.get('category') || 'all'
  const unit = sp.get('unit') || 'all'

  const refs = useRefs()
  const fields = useFields('deal')

  const params: AnalyticsParams = useMemo(() => ({
    period,
    category: category === 'all' ? 'all' : Number(category),
    unit,
    months: 13,
  }), [period, category, unit])

  const q = useQuery({
    queryKey: ['analytics-sales', params],
    queryFn: () => fetchSalesAnalytics(params),
    // Агрегат по всей базе — не то, что стоит пересчитывать на каждый фокус окна.
    staleTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof NotWired) && count < 1,
    placeholderData: (prev) => prev,
  })

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(sp)
    if (v && v !== 'all') next.set(k, v)
    else next.delete(k)
    setSp(next, { replace: true })
  }

  const d = q.data

  return (
    <>
      <div className="page-head">
        <h1>Аналитика продаж</h1>
        <div className="spacer" />
        <a href="https://55.cirspb.com/#/analytics" target="_blank" rel="noreferrer">
          тот же раздел на портале 55 →
        </a>
      </div>

      {q.error instanceof NotWired ? (
        <div className="notice">
          <b>Раздел ещё не подключён.</b> Экран готов, агрегаты в ядре готовы, но маршрута
          {' '}<span className="mono">POST /api/analytics/sales</span> в BFF пока нет — а
          {' '}именно он подставляет зону видимости. Пока его не добавят, показать цифры
          нечем: спрашивать ядро напрямую из браузера мы не можем и не должны.
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <FilterBar id="analytics">
            <div className="f">
              <label htmlFor="an-period">Период</label>
              <select id="an-period" value={period} onChange={(e) => set('period', e.target.value)}>
                {PERIODS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}{p.slow ? ' (считается дольше)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="f">
              <label htmlFor="an-category">Воронка</label>
              <select id="an-category" value={category}
                      onChange={(e) => set('category', e.target.value)}>
                <option value="all">Все воронки</option>
                {refs.categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="f">
              <label htmlFor="an-unit">Бизнес-юнит</label>
              <select id="an-unit" value={unit} onChange={(e) => set('unit', e.target.value)}
                      disabled={!fields.loaded}>
                <option value="all">Все</option>
                <option value="empty">Не заполнен</option>
                {(fields.meta('UF_CRM_1570972880')?.enum ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{o.value}</option>
                ))}
              </select>
            </div>
          </FilterBar>
          <div className="hint" style={{ marginTop: 10 }}>
            {d ? (
              <>
                Период: <b>{d.period.label}</b> — по дате закрытия сделки, с {d.period.from} по
                {' '}{d.period.to} (не включая). Ядро посчитало за {fmtNum(d.ms)} мс.
                {d.filter.scope.kind === 'ids' && (
                  <> Выборка ограничена вашей зоной видимости: {d.filter.scope.count}
                    {' '}{plural(d.filter.scope.count ?? 0, 'сотрудник', 'сотрудника', 'сотрудников')}.</>
                )}
              </>
            ) : 'Считаем…'}
          </div>
        </div>
      )}

      {!(q.error instanceof NotWired) && (
        <QueryState isLoading={q.isLoading} error={q.error}>
          {d && (
            <>
              {d.filter.scope.empty && (
                <div className="notice">
                  Ваша учётная запись не связана с сотрудником Bitrix, поэтому зона видимости
                  пуста и в аналитику не попадает ни одна сделка. Пустые цифры ниже — это не
                  «продаж нет», а «вам пока ничего не видно».
                </div>
              )}

              {/* ─────────── ЗАКРЫТО ЗА ПЕРИОД ─────────── */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2>Закрыто за {d.period.label}</h2>
                  <div className="spacer" />
                  {d.period.prev_label && (
                    <span className="hint">сравнение: {d.period.prev_label}</span>
                  )}
                </div>

                <div className="tiles">
                  <div className="tile">
                    <div className="t-label">Выиграно</div>
                    <div className="t-sum">{fmtMoney(d.closed.won.sum)}</div>
                    <div className="t-label">
                      {fmtNum(d.closed.won.count)}
                      {' '}{plural(d.closed.won.count, 'сделка', 'сделки', 'сделок')}
                      {d.dynamics && (
                        <> · <span className={`badge ${deltaKind(d.dynamics.won_sum)}`}>
                          {fmtDelta(d.dynamics.won_sum)}
                        </span></>
                      )}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Средний чек</div>
                    <div className="t-sum">
                      {d.closed.avg_check === null ? '—' : fmtMoney(d.closed.avg_check)}
                    </div>
                    <div className="t-label">
                      по выигранным
                      {d.dynamics && (
                        <> · <span className={`badge ${deltaKind(d.dynamics.avg_check)}`}>
                          {fmtDelta(d.dynamics.avg_check)}
                        </span></>
                      )}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Конверсия</div>
                    <div className="t-sum">{fmtPct(d.closed.conversion_count)}</div>
                    <div className="t-label">
                      выиграно из {fmtNum(d.closed.closed_count)} закрытых
                      {d.dynamics && (
                        <> · <span className={`badge ${deltaKind(d.dynamics.conversion_count_pp)}`}>
                          {fmtDeltaPp(d.dynamics.conversion_count_pp)}
                        </span></>
                      )}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Проиграно</div>
                    <div className="t-sum">{fmtMoney(d.closed.lost.sum)}</div>
                    <div className="t-label">
                      {fmtNum(d.closed.lost.count)}
                      {' '}{plural(d.closed.lost.count, 'сделка', 'сделки', 'сделок')}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Конверсия по деньгам</div>
                    <div className="t-sum">{fmtPct(d.closed.conversion_sum)}</div>
                    <div className="t-label">доля выигранных сумм</div>
                  </div>
                </div>

                <div className="hint" style={{ marginTop: 10 }}>
                  Конверсия — доля выигранных среди <b>закрытых за период</b>, ровно как в
                  портале 55. Это не «конверсия воронки»: открытые сделки в неё не входят
                  вовсе, поэтому цифра отвечает на вопрос «из того, что решилось, сколько
                  наше», а не «сколько запросов доходит до денег».
                  {d.closed_prev && (
                    <> За {d.period.prev_label} было {fmtMoney(d.closed_prev.won.sum)} по
                      {' '}{fmtNum(d.closed_prev.won.count)}
                      {' '}{plural(d.closed_prev.won.count, 'сделке', 'сделкам', 'сделкам')}
                      {' '}при конверсии {fmtPct(d.closed_prev.conversion_count)}.</>
                  )}
                  {!d.period.prev_label && ' Сравнивать не с чем: у этого периода нет предыдущего.'}
                </div>
              </div>

              {/* ─────────── ОТКРЫТЫЙ ПОРТФЕЛЬ И ВОРОНКА ─────────── */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2>Открытый портфель</h2>
                  <div className="spacer" />
                  <span className="hint">сделки, которые ещё не закрыты — на сегодня</span>
                </div>

                <div className="tiles">
                  <div className="tile">
                    <div className="t-label">В работе</div>
                    <div className="t-sum">{fmtMoney(d.portfolio.sum)}</div>
                    <div className="t-label">
                      {fmtNum(d.portfolio.count)}
                      {' '}{plural(d.portfolio.count, 'сделка', 'сделки', 'сделок')}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Стадий занято</div>
                    <div className="t-sum">{fmtNum(d.portfolio.by_stage.length)}</div>
                    <div className="t-label">из всех воронок вместе</div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Людей с портфелем</div>
                    <div className="t-sum">{fmtNum(d.portfolio.by_user.length)}</div>
                    <div className="t-label">по ответственному в сделке</div>
                  </div>
                </div>

                {d.portfolio.other_currency_count > 0 && (
                  <div className="hint" style={{ marginTop: 10 }}>
                    В портфеле есть {d.portfolio.other_currency_count}
                    {' '}{plural(d.portfolio.other_currency_count, 'сделка', 'сделки', 'сделок')}
                    {' '}в валюте ({d.portfolio.other_currencies.join(', ')}) — и с 27.07.2026
                    {' '}они в сумму <b>входят</b>. Это не сложение рублей с евро: пересчёт
                    {' '}делает сам бизнес-процесс Bitrix, и в сумме сделки лежат уже рубли
                    {' '}даже у сделок, помеченных EUR или USD. Раньше такие сделки
                    {' '}обнулялись, и портфель был меньше на 1,8 млн ₽.
                  </div>
                )}

                <h2 style={{ marginTop: 16 }}>Воронка по стадиям</h2>
                <FunnelTable rows={d.portfolio.by_stage} stageName={refs.stageName} />

                <div className="hint" style={{ marginTop: 10 }}>
                  Портал 55 считает эту же воронку по первым 600 открытым сделкам (его обход
                  останавливается на 12-й странице), а открытых сейчас {fmtNum(d.portfolio.count)}.
                  {' '}Здесь суммы по <b>всем</b> — если цифры не сойдутся с порталом, дело в этом.
                </div>
              </div>

              {/* ─────────── ПО МЕСЯЦАМ ─────────── */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2>По месяцам</h2>
                  <div className="spacer" />
                  <span className="hint">с {fmtMonth(d.months_from.slice(0, 7))}, по дате закрытия</span>
                </div>
                {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
                <div className="table-wrap cards">
                  <table>
                    <thead>
                      <tr>
                        <th>Месяц</th>
                        <th className="num">Выиграно</th>
                        <th className="num">Сделок</th>
                        <th className="num">Средний чек</th>
                        <th className="num">Конверсия</th>
                        <th className="num">Проиграно</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.by_month.length === 0 && (
                        <tr>
                          <td colSpan={6} className="empty">
                            <div className="empty-title">За этот отрезок закрытых сделок нет.</div>
                          </td>
                        </tr>
                      )}
                      {[...d.by_month].reverse().map((m) => (
                        <tr key={m.month}>
                          <td data-label="Месяц">{fmtMonth(m.month)}</td>
                          <td className="num" data-label="Выиграно">{fmtMoney(m.won_sum)}</td>
                          <td className="num" data-label="Сделок">{fmtNum(m.won_count)}</td>
                          <td className="num" data-label="Средний чек">
                            {m.avg_check === null ? '—' : fmtMoney(m.avg_check)}
                          </td>
                          <td className="num" data-label="Конверсия">{fmtPct(m.conversion_count)}</td>
                          <td className="num" data-label="Проиграно">{fmtMoney(m.lost_sum)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="hint" style={{ marginTop: 10 }}>
                  Месяц — <b>календарный</b>, по дате закрытия сделки. В exec-панели «месяц»
                  значит <b>отчётный</b> месяц (пара русских enum-полей, которые заполняют
                  руками), и это другое число. С бухгалтерским отчётом эта таблица совпадать
                  не обязана — совпадения ждать не надо, надо знать, что считается.
                </div>
              </div>

              {/* ─────────── ВОРОНКИ И ЮНИТЫ ─────────── */}
              <div className="grid-2" style={{ marginBottom: 16 }}>
                <div className="card">
                  <h2>По бизнес-юнитам</h2>
                  <UnitTable rows={d.by_unit} label={(u) => unitLabel(u, fields)} />
                  <UnitNote rows={d.by_unit} loaded={fields.loaded} />
                </div>
                <div className="card">
                  <h2>По воронкам</h2>
                  <div className="table-wrap cards">
                    <table>
                      <thead>
                        <tr>
                          <th>Воронка</th>
                          <th className="num">Выиграно</th>
                          <th className="num">Сделок</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.by_category.length === 0 && (
                          <tr>
                            <td colSpan={3} className="empty">
                              <div className="empty-title">Выигранных сделок за период нет.</div>
                            </td>
                          </tr>
                        )}
                        {d.by_category.map((c) => (
                          <tr key={String(c.category_id)}>
                            <td data-label="Воронка">{refs.categoryName(String(c.category_id))}</td>
                            <td className="num" data-label="Выиграно">{fmtMoney(c.sum)}</td>
                            <td className="num" data-label="Сделок">{fmtNum(c.count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ─────────── ЛЮДИ ─────────── */}
              <div className="grid-2" style={{ marginBottom: 16 }}>
                <div className="card">
                  <div className="page-head" style={{ marginBottom: 10 }}>
                    <h2>Кто закрыл за период</h2>
                    <div className="spacer" />
                    <Link to="/sales-rating">рейтинг ОП →</Link>
                  </div>
                  <PeopleTable rows={d.by_user} userName={refs.userName} what="Выиграно" />
                </div>
                <div className="card">
                  <h2>Нагрузка: чей открытый портфель</h2>
                  <PeopleTable rows={d.portfolio.by_user} userName={refs.userName} what="В работе" />
                  <div className="hint" style={{ marginTop: 10 }}>
                    Это <b>ответственный в сделке</b>, а не подчинённость. Отдел человека и
                    его зона ответственности за деньги в Bitrix не связаны: у руководителя
                    сервиса СПб (замер 28.07.2026) 16 действующих сотрудников в самом отделе
                и ноль своей дебиторки, потому что деньги
                    по городу висят на координаторах чужого отдела.
                  </div>
                </div>
              </div>

              {/* ─────────── ОГОВОРКИ ОТ ЯДРА ─────────── */}
              <div className="card">
                <h2>Что именно посчитано</h2>
                <ul className="plain-list">
                  {d.notes.map((n, i) => <li key={i} className="hint">{n}</li>)}
                </ul>
              </div>
            </>
          )}
        </QueryState>
      )}
    </>
  )
}

// ─────────────────────────── таблицы ───────────────────────────

/**
 * Воронка: стадия, сумма, число сделок и полоска доли от самой крупной стадии.
 *
 * Полоска нарисована фоном из токена дизайн-системы (`--accent-soft`), своих цветов не
 * вводим. Она нужна не для красоты: 15 строк одинаково выглядящих чисел не читаются, а
 * «где застряли деньги» — главный вопрос к этой таблице.
 */
function FunnelTable({ rows, stageName }: {
  rows: Array<{ stage_id: string; count: number; sum: number; semantics: string }>
  stageName: (id?: string | null) => string
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.sum), 0)
  if (rows.length === 0) {
    return <div className="empty"><div className="empty-title">Открытых сделок нет</div></div>
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Стадия</th>
            <th className="num">Сумма</th>
            <th className="num">Сделок</th>
            <th className="num">Доля</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const доля = shareOfMax(r.sum, max)
            return (
              <tr key={r.stage_id}>
                <td data-label="Стадия" style={{
                  background: `linear-gradient(to right, var(--accent-soft) ${доля}%,`
                    + ` transparent ${доля}%)`,
                }}>
                  {stageName(r.stage_id)}
                  <span className="faint mono" style={{ marginLeft: 8 }}>{r.stage_id}</span>
                </td>
                <td className="num" data-label="Сумма">{fmtMoney(r.sum)}</td>
                <td className="num" data-label="Сделок">{fmtNum(r.count)}</td>
                <td className="num" data-label="Доля">{доля} %</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function UnitTable({ rows, label }: { rows: UnitRow[]; label: (u: string) => string }) {
  if (rows.length === 0) {
    return <div className="empty"><div className="empty-title">Выигранных сделок за период нет</div></div>
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Бизнес-юнит</th>
            <th className="num">Выиграно</th>
            <th className="num">Сделок</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.unit || 'empty'}>
              <td data-label="Бизнес-юнит">
                {r.empty ? <span className="muted">{label('')}</span> : label(r.unit)}
              </td>
              <td className="num" data-label="Выиграно">{fmtMoney(r.sum)}</td>
              <td className="num" data-label="Сделок">{fmtNum(r.count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Почему у бизнес-юнита свой абзац: это поле уже ломало отчётность, и молчать о нём нельзя. */
function UnitNote({ rows, loaded }: { rows: UnitRow[]; loaded: boolean }) {
  const пусто = emptyUnitShare(rows)
  return (
    <div className="hint" style={{ marginTop: 10 }}>
      {пусто.count > 0 && (
        <>
          У <b>{fmtNum(пусто.count)}</b>
          {' '}{plural(пусто.count, 'сделки', 'сделок', 'сделок')} бизнес-юнит{' '}
          <b>не заполнен</b> — это {fmtPct(пусто.pct)} выручки за период. Такая строка
          выпадает из любого разреза «по подразделению», и её надо видеть, а не списывать
          в «прочее».{' '}
        </>
      )}
      Группировка идёт по <b>идентификатору</b> значения перечисления Bitrix, а не по
      названию. Это важно: в exec-панели тот же юнит хранился текстом и разъезжался в
      написаниях («Автоматика РнД» против «РнД Автоматика»), из-за чего строгий отбор по
      названию показывал четырём руководителям нули. Здесь такого быть не может.
      {!loaded && ' Справочник полей не пришёл, поэтому вместо названий показаны коды.'}
    </div>
  )
}

function PeopleTable({ rows, userName, what }: {
  rows: Array<{ user_id: number | null; count: number; sum: number }>
  userName: (id?: string | number | null) => string
  what: string
}) {
  if (rows.length === 0) {
    return <div className="empty"><div className="empty-title">Строк нет</div></div>
  }
  const показать = rows.slice(0, ROWS)
  return (
    <>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Сотрудник</th>
              {/* Подпись второй колонки задаёт вызывающий («Выиграно» или «В работе»), и в
                  карточке она берётся из того же `what`: своей копии здесь быть не должно. */}
              <th className="num">{what}</th>
              <th className="num">Сделок</th>
            </tr>
          </thead>
          <tbody>
            {показать.map((r) => (
              <tr key={String(r.user_id)}>
                <td data-label="Сотрудник">{r.user_id === null
                  ? <span className="muted">Без ответственного</span>
                  : userName(r.user_id)}</td>
                <td className="num" data-label={what}>{fmtMoney(r.sum)}</td>
                <td className="num" data-label="Сделок">{fmtNum(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > ROWS && (
        <div className="hint" style={{ marginTop: 8 }}>
          Показаны {ROWS} из {fmtNum(rows.length)} — итоги выше посчитаны по всем.
        </div>
      )}
    </>
  )
}
